import type { ConsumableReplenishment, ModuleEndpoint, User } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 관리 쓰기 — 사용자 · 세션 · 모듈 운영.
 * ✅ 계정 쓰기 넷은 실물로 확인했다(2026-09-28 로컬 · 히나): `PUT /users/{id}` 200 · `POST …/password` 204 ·
 *    `…/lock` · `…/unlock` 200. 나머지(세션 · 모듈 운영)는 요청 모양이 계약 기준이다.
 * ⚠ 역할 값은 **계약 enum 3종**만 보낼 수 있다. 화면의 `service` 는 서버가 모른다.
 */

type WritableRole = 'admin' | 'operator' | 'viewer'

export const useCreateUser = createCommand<
  { username: string; name: string; role: WritableRole; password: string; commandId?: string },
  User
>({
  send: ({ username, name, role, password, commandId }) =>
    orchestrator.request<User>({
      method: 'POST',
      path: '/users',
      body: { command_id: commandId ?? newCommandId(), username, name, role, password },
      retryOn401: true,
    }),
  invalidates: () => [keys.users.all()],
})

/**
 * 계정 수정·역할 변경 — `PUT /users/{id}`(`UserUpdateEnvelope`).
 *
 * 🔴 **`username` · `name` · `role` 셋 다 필수다**(실서버 스키마 · 2026-09-28 실측).
 *    종전에는 `role` 만 실어 보내 **역할 변경이 언제나 422 `REQUEST_VALIDATION_FAIL`** 이었다.
 * 🔴 **전체 교체다** — 서버가 `.values(name=body.name, role=body.role)` 로 통째 덮는다
 *    (`ctcells-core` `views/users.py` `update_user`). 역할만 바꿔도 `name` 은 **받은 값 그대로** 싣는다.
 *    `username` 이 받은 값과 다르거나 `password` 를 실으면 422 `USER_VALIDATION_FAIL` 이다 — 싣지 않는다.
 */
export const useUpdateUser = createCommand<
  { userId: string; username: string; name: string; role: WritableRole; commandId?: string },
  User
>({
  send: ({ userId, username, name, role, commandId }) =>
    orchestrator.request<User>({
      method: 'PUT',
      path: `/users/${userId}`,
      body: { command_id: commandId ?? newCommandId(), username, name, role },
      retryOn401: true,
    }),
  invalidates: ({ userId }) => [keys.users.detail(userId), keys.users.all()],
})

/**
 * 계정 삭제 — 계약상 **soft-delete(비활성)** 다(`deleteUser` · UAM-ACC-05). 응답 200 은 반영본이고 멱등이다.
 *
 * 🔴 **본문이 `IrreversibleCommandEnvelope` 라 `reason` 이 필수다**(minLength 1).
 *    되돌릴 수 없는 명령군 전용 봉투이고, 사유는 `audit_log.detail.reason` 으로 해시체인에 봉인된다.
 *    빠뜨리면 422 `REQUEST_VALIDATION_FAIL` 이다 — 화면이 사유를 받아 넘겨야 한다.
 * ⚠ 마지막 관리자는 서버가 409 `LAST_ADMIN_PROTECT` 로 막는다. **프론트가 미리 세지 않는다**.
 * ⚠ 재활성 경로는 계약에 없다(`User.status` 주석 — LWM-CAT-* 가 복구를 정의하지 않는다).
 */
export const useDeleteUser = createCommand<
  { userId: string; reason: string; commandId?: string },
  User
>({
  send: ({ userId, reason, commandId }) =>
    orchestrator.request<User>({
      method: 'DELETE',
      path: `/users/${userId}`,
      body: { command_id: commandId ?? newCommandId(), reason },
      retryOn401: true,
    }),
  invalidates: ({ userId }) => [keys.users.detail(userId), keys.users.all()],
})

/** 계정 잠금(UAM-ACC-06). ⚠ 마지막 관리자는 서버가 409 `LAST_ADMIN_PROTECT` 로 막는다 */
export const useLockUser = createCommand<{ userId: string; commandId?: string }, User>({
  send: ({ userId, commandId }) =>
    orchestrator.request<User>({
      method: 'POST',
      path: `/users/${userId}/lock`,
      body: { command_id: commandId ?? newCommandId() },
      retryOn401: true,
    }),
  invalidates: ({ userId }) => [keys.users.detail(userId), keys.users.all()],
})

/** 계정 잠금 해제(UAM-ACC-06). 응답은 해제 반영본이다 */
export const useUnlockUser = createCommand<{ userId: string; commandId?: string }, User>({
  send: ({ userId, commandId }) =>
    orchestrator.request<User>({
      method: 'POST',
      path: `/users/${userId}/unlock`,
      body: { command_id: commandId ?? newCommandId() },
      retryOn401: true,
    }),
  invalidates: ({ userId }) => [keys.users.detail(userId), keys.users.all()],
})

/**
 * 비밀번호 재설정.
 * 🔴 **본인 재설정이면 `currentPassword` 가 필수**이고 관리자 강제 재설정은 생략한다.
 *    조건이 「호출자가 누구인가」라서 스키마가 집행하지 못한다 — 집행은 핸들러(422)다.
 */
export const useResetPassword = createCommand<
  { userId: string; newPassword: string; currentPassword?: string; commandId?: string },
  void
>({
  send: ({ userId, newPassword, currentPassword, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/users/${userId}/password`,
      body: {
        command_id: commandId ?? newCommandId(),
        new_password: newPassword,
        ...(currentPassword === undefined ? {} : { current_password: currentPassword }),
      },
      retryOn401: true,
    }),
  invalidates: ({ userId }) => [keys.users.detail(userId)],
})

/**
 * 세션 강제 종료(UAM-SES-03). 즉시 폐기이고 응답은 204(본문 없음)다.
 *
 * 🔴 **`DELETE` 인데 본문이 필수다**(`revokeSession` · `requestBody.required: true` ·
 *    `CommandEnvelope`). 예전에는 본문 없이 보내 **전건 422 `REQUEST_VALIDATION_FAIL`** 이었다.
 *    메서드만 보고 「DELETE 는 본문이 없다」고 넘겨짚지 마라 — 이 계약은 멱등키를 본문으로 받는다.
 * ⚠ 사용자 목록의 「세션 수」 열이 이 결과로 바뀐다. 사용자 키도 같이 비운다.
 */
export const useRevokeSession = createCommand<{ sessionId: string; commandId?: string }, void>({
  send: ({ sessionId, commandId }) =>
    orchestrator.request<void>({
      method: 'DELETE',
      path: `/sessions/${sessionId}`,
      body: { command_id: commandId ?? newCommandId() },
      retryOn401: true,
    }),
  invalidates: () => [keys.auth.all(), keys.users.all()],
})

/** 🔴 `X-Control-Token` 대상. 「시스템 ↔ 단독」 제어 주체 전환이다 */
export const useSetModuleControlOwner = createCommand<
  { moduleId: string; owner: 'orchestrator' | 'local'; commandId?: string },
  void
>({
  send: ({ moduleId, owner, commandId }) =>
    orchestrator.request<void>({
      method: 'PUT',
      path: `/modules/${moduleId}/control-owner`,
      body: { command_id: commandId ?? newCommandId(), control_owner: owner },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

/**
 * 소모품 보충 기록(IFC-RO-32) — L2 가 모듈 `/consumables/replenishment`(IFC-RM-20)로 중계해 **모듈의 카운터를
 * 그 값으로 덮는다.** 응답 201 은 보충 원장 1건(`ConsumableReplenishment`)이다. 계약상 **제어락 비대상**이다.
 *
 * 🔴 **요청 필드는 `count` 다 — `count_after` 가 아니다**(실서버 `RecordModuleConsumableReplenishmentBody`
 *    2026-09-28 실측). `count_after` 는 **응답**(원장 행) 쪽 이름이다. 종전 훅이 요청에 `count_after` 를 실어
 *    서버가 그 키를 버렸고, `count` 가 빠진 요청이 되어 **전건 422** 였다(부르는 화면이 없어 아무도 몰랐다).
 * 🔴 **`count` 는 «더하기»가 아니라 «보충 후 잔량»이다.** 192 를 보내면 192 개가 된다.
 * 🔴 **`count` 를 필수로 받는다 — 계약은 생략 가능이라 적었지만 실물이 못 받는다.** 계약 문면은 「생략 시
 *    capability 만재값」인데 module-core 에 그 만재값 상수가 없어 생략을 **조건 없이** 422
 *    `REQUEST_VALIDATION_FAIL` 로 거절한다(`module-core/app/api/lifecycle.py` · 2026-09-28 실측 — `staining-1 tip` ·
 *    `assembler-1 tc` 둘 다 422). 생략을 허락하면 반드시 실패하는 요청을 타입이 막지 못한다.
 */
export const useRecordConsumableReplenishment = createCommand<
  {
    moduleId: string
    kind: 'tip' | 'tc'
    /** 보충 후 잔량(요청 필드 `count`). 응답의 `count_after` 와 같은 값이 되돌아온다 */
    count: number
    note?: string
    commandId?: string
  },
  ConsumableReplenishment
>({
  send: ({ moduleId, kind, count, note, commandId }) =>
    orchestrator.request<ConsumableReplenishment>({
      method: 'POST',
      path: `/modules/${moduleId}/consumables/replenishment`,
      body: {
        command_id: commandId ?? newCommandId(),
        kind,
        count,
        ...(note === undefined ? {} : { note }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  // 보충 이력(`keys.replenishments`)도 — 관리 › 로그 「보충 기록」 탭이 새 줄을 받는다
  invalidates: ({ moduleId }) => [
    keys.modules.detail(moduleId),
    keys.modules.all(),
    keys.replenishments.all(),
  ],
})

/**
 * 시약 보충 기록 — 같은 창구(`POST /modules/{id}/consumables/replenishment`)의 `kind: 'reagent'`.
 * 🔴 본문은 `reagent {labwareId, wellNames, volume}` 이고 `count` 를 싣지 않는다(서버 「kind=reagent 에 count 를 주면 422」).
 * 🔴 `volume` 은 «보충 후 웰마다 양»(µL)이다 — 더하기가 아니라 맞춤. 웰마다 양이 다르면 나눠 보낸다(서버 `ReagentReplenishment`).
 * 🔴 소진 보류(`hold.kind = reagent_exhausted`)는 그 보류가 가리킨 웰이 «다» 든 보충이 와야 모듈이 걷는다 — 그 보류가 서 있으면
 *    염색기가 슬라이드 이송 전 걸쇠 확인을 건너뛰어 6축 L 이송이 `STAIN_LATCH_FAIL` 로 보상 정지했다(dev2 2026-10-08 세 번).
 */
export const useRecordReagentReplenishment = createCommand<
  {
    moduleId: string
    labwareId: string
    wellNames: readonly string[]
    /** 보충 후 웰마다 양(µL) */
    volume: number
    note?: string
    commandId?: string
  },
  ConsumableReplenishment
>({
  send: ({ moduleId, labwareId, wellNames, volume, note, commandId }) =>
    orchestrator.request<ConsumableReplenishment>({
      method: 'POST',
      path: `/modules/${moduleId}/consumables/replenishment`,
      body: {
        command_id: commandId ?? newCommandId(),
        kind: 'reagent',
        reagent: { labwareId, wellNames: [...wellNames], volume },
        ...(note === undefined ? {} : { note }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  // 보충 이력(`keys.replenishments`)도 — 관리 › 로그 「보충 기록」 탭이 새 줄을 받는다
  invalidates: ({ moduleId }) => [
    keys.modules.detail(moduleId),
    keys.modules.all(),
    keys.replenishments.all(),
  ],
})

/** 서비스 종착 선언. 🔴 사유(진단) 필수 — 되돌릴 수 없는 운영 선언이다 */
export const useTerminateModuleService = createCommand<
  { moduleId: string; reason: string; commandId?: string },
  void
>({
  send: ({ moduleId, reason, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/modules/${moduleId}/service/terminate`,
      body: { command_id: commandId ?? newCommandId(), reason },
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

export const useReinstateModuleService = createCommand<
  { moduleId: string; reason: string; commandId?: string },
  void
>({
  send: ({ moduleId, reason, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/modules/${moduleId}/service/reinstate`,
      body: { command_id: commandId ?? newCommandId(), reason },
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

/** 모듈 접속 주소 수동 지정. `manual` 로 바뀌면 자동 발견이 덮지 않는다 */
export const useSetModuleEndpoint = createCommand<
  { moduleId: string; host: string; port?: number; commandId?: string },
  ModuleEndpoint
>({
  send: ({ moduleId, host, port, commandId }) =>
    orchestrator.request<ModuleEndpoint>({
      method: 'PUT',
      path: `/modules/${moduleId}/endpoint`,
      body: {
        command_id: commandId ?? newCommandId(),
        host,
        ...(port === undefined ? {} : { port }),
      },
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId)],
})
